import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import ModernHeader from './components/ModernHeader';
import ResponsiveFooter from './components/ResponsiveFooter';
import AIAssistant from './components/AIAssistant';
import ModernBottomNav from './components/ModernBottomNav';
import BackToTopButton from './components/BackToTopButton';
import NewBusinessAlert from './components/NewBusinessAlert';
import PushEngagementPrompt from './components/PushEngagementPrompt';
import ProtectedRoute from './components/ProtectedRoute';
import KeyboardShortcuts from './components/KeyboardShortcuts';
import InactivityTimer from './components/InactivityTimer';
import RouteMetadata from './components/RouteMetadata';
import { BusinessListSkeleton } from './components/SkeletonLoader';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { AdvertisementProvider } from './contexts/AdvertisementContext';
import { PartnerProvider } from './contexts/PartnerContext';
import { TestimonialProvider } from './contexts/TestimonialContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ReviewProvider } from './contexts/ReviewContext';
import { FavoritesProvider } from './contexts/FavoritesContext';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import { BookingProvider } from './contexts/BookingContext';
import { EventProvider } from './contexts/EventContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { AIAssistantProvider } from './contexts/AIAssistantContext';
import { MarketingProvider } from './contexts/MarketingContext';
import { ChatProvider } from './contexts/ChatContext';
import { NewsProvider } from './contexts/NewsContext';
import { WalletProvider } from './contexts/WalletContext';
import NewsPage from './pages/NewsPage';
import { applySafariNavigationFix } from './utils/safariNavigationFix';
import { ADMIN_PANEL_PATH, ADMIN_ROLE_MANAGER_PATH } from './config/adminRoutes';
import './styles/theme.css';
import './styles/responsive.css';
import './styles/animations.css';
import './styles/app-pages.css';

// Lazy loading des pages pour améliorer les performances
const UltraModernHomePage = lazy(() => import('./pages/UltraModernHomePage'));
const ModernHomePageNew = lazy(() => import('./pages/ModernHomePageNew'));
const ProfessionalHomePage = lazy(() => import('./pages/ProfessionalHomePage'));
const AllCategoriesPageModern = lazy(() => import('./pages/AllCategoriesPageModern'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const BusinessRegistrationPage = lazy(() => import('./pages/BusinessRegistrationPage'));
const CategoryPageModern = lazy(() => import('./pages/CategoryPageModern'));
const VivreEnGuineePageModern = lazy(() => import('./pages/VivreEnGuineePageModern'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const AdminSetupPage = lazy(() => import('./pages/AdminSetupPage'));
const AdminRoleFixPage = lazy(() => import('./pages/AdminRoleFixPage'));
const AdminRoleManagerPage = lazy(() => import('./pages/AdminRoleManagerPage'));
const BookingsPage = lazy(() => import('./pages/BookingsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const CarRentalPage = lazy(() => import('./pages/CarRentalPage'));
const CarRentalRegistrationPage = lazy(() => import('./pages/CarRentalRegistrationPage'));
const DashboardRentalPage = lazy(() => import('./pages/DashboardRentalPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const ActivitiesPage = lazy(() => import('./pages/ActivitiesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AlloPage = lazy(() => import('./pages/AlloPage'));
const TaxiRegistrationPage = lazy(() => import('./pages/TaxiRegistrationPage'));
const TaxiTermsPage = lazy(() => import('./pages/TaxiTermsPage'));
const RentalTermsPage = lazy(() => import('./pages/RentalTermsPage'));
const ActivityTermsPage = lazy(() => import('./pages/ActivityTermsPage'));
const BusinessTermsPage = lazy(() => import('./pages/BusinessTermsPage'));
const CommercialAgentTermsPage = lazy(() => import('./pages/CommercialAgentTermsPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const DashboardClient = lazy(() => import('./pages/DashboardClient'));
const DashboardDriver = lazy(() => import('./pages/DashboardDriver'));
const DashboardBusinessPage = lazy(() => import('./pages/DashboardBusinessPage'));
const TicketScannerPage = lazy(() => import('./pages/TicketScannerPage'));
const ActivityInvitesPage = lazy(() => import('./pages/ActivityInvitesPage'));
const ActivitySaleQrPage = lazy(() => import('./pages/ActivitySaleQrPage'));
const ManageActivityTicketsPage = lazy(() => import('./pages/ManageActivityTicketsPage'));
const InviteClaimPage = lazy(() => import('./pages/InviteClaimPage'));
const PublicTicketSalePage = lazy(() => import('./pages/PublicTicketSalePage'));
const SelectDashboardPage = lazy(() => import('./pages/SelectDashboardPage'));
const MarketingPage = lazy(() => import('./pages/MarketingPage'));
const CompleteProfilePage = lazy(() => import('./pages/CompleteProfilePage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ActivityRegistrationPage = lazy(() => import('./pages/ActivityRegistrationPage'));
const BusinessDetailPage = lazy(() => import('./pages/BusinessDetailPage'));
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EcosystemPage = lazy(() => import('./pages/EcosystemPage'));
const HealthBookingPage = lazy(() => import('./pages/HealthBookingPage'));
const WalletPage = lazy(() => import('./pages/WalletPage'));
const CinemaTicketsPage = lazy(() => import('./pages/CinemaTicketsPage'));
const CinemaDetailPage = lazy(() => import('./pages/CinemaDetailPage'));
const CinemaManagerPage = lazy(() => import('./pages/CinemaManagerPage'));
const HotelsManagerPage = lazy(() => import('./pages/HotelsManagerPage'));
const TourismTicketsPage = lazy(() => import('./pages/TourismTicketsPage'));
const TourismSiteDetailPage = lazy(() => import('./pages/TourismSiteDetailPage'));
const TourismManagerPage = lazy(() => import('./pages/TourismManagerPage'));
const RemoteWalletTopUpPage = lazy(() => import('./pages/RemoteWalletTopUpPage'));
const MerchantCollectPage = lazy(() => import('./pages/MerchantCollectPage'));
const MerchantPayPage = lazy(() => import('./pages/MerchantPayPage'));
const MerchantScanPage = lazy(() => import('./pages/MerchantScanPage'));
const DashboardCashAgentPage = lazy(() => import('./pages/DashboardCashAgentPage'));
const BecomeCommercialAgentPage = lazy(() => import('./pages/BecomeCommercialAgentPage'));
const CommercialAgentDashboardPage = lazy(() => import('./pages/CommercialAgentDashboardPage'));
const AgentChannelPage = lazy(() => import('./pages/AgentChannelPage'));

// Composant interne pour utiliser useLocation
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Enregistrer le service worker pour les notifications push
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          updateViaCache: 'none'
        });
        if (!cancelled && registration.update) {
          await registration.update();
        }
      } catch (error) {
        console.error("Échec de l'enregistrement du Service Worker:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  
  // Pas de correctif global - laisser React Router gérer naturellement
  // Les composants utilisent SafariLink si nécessaire pour Safari
  
  // Forcer le scroll vers le haut et le re-render à chaque changement de route
  useEffect(() => {
    // Scroll vers le haut immédiatement
    window.scrollTo(0, 0);
    // Forcer un re-render en déclenchant un événement personnalisé
    window.dispatchEvent(new Event('resize'));
    // Forcer le reflow pour Safari
    void document.body.offsetHeight;
    
    // Forcer la mise à jour de tous les composants après navigation
    // Utiliser requestAnimationFrame pour s'assurer que le DOM est mis à jour
    requestAnimationFrame(() => {
      // Déclencher un événement personnalisé pour notifier les composants
      window.dispatchEvent(new CustomEvent('routechange', { 
        detail: { pathname: location.pathname } 
      }));
    });
  }, [location.pathname]);
  
  return (
    <div className="App">
                    <a className="skip-link" href="#main-content">
                      Aller au contenu
                    </a>
                    <RouteMetadata />
                    <ModernHeader />
                    <NewBusinessAlert />
                    <PushEngagementPrompt />
                    <main
                      id="main-content"
                      className="app-main"
                      tabIndex="-1"
                      style={{ paddingBottom: '80px', minHeight: 'calc(100vh - 70px)' }}
                    >
                      <ErrorBoundary>
                        <Suspense
                          fallback={
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: '60vh',
                              padding: '2rem'
                            }}>
                              <BusinessListSkeleton count={3} />
                            </div>
                          }
                        >
                          <Routes key={location.pathname}>
              <Route path="/" element={<UltraModernHomePage key="home" />} />
              <Route path="/categories" element={<AllCategoriesPageModern key="categories" />} />
              <Route path="/all-categories" element={<AllCategoriesPageModern key="all-categories" />} />
              <Route path="/category/:categorySlug" element={<CategoryPageModern key={`category-${location.pathname}`} />} />
              <Route path="/traffic" element={<Navigate to="/" replace />} />
              <Route path="/traffic-map" element={<Navigate to="/" replace />} />
              <Route path="/vivre-en-guinee" element={<VivreEnGuineePageModern key="vivre-en-guinee" />} />
              <Route path="/ecosystem" element={<EcosystemPage key="ecosystem" />} />
              <Route path="/devenir-agent" element={<BecomeCommercialAgentPage key="devenir-agent" />} />
              <Route
                path="/dashboard-agent"
                element={
                  <ProtectedRoute key="dashboard-agent">
                    <CommercialAgentDashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/a/:code" element={<AgentChannelPage key="agent-channel" />} />
              <Route path="/ecosystem/sante" element={<HealthBookingPage key="ecosystem-sante" />} />
              <Route path="/ecosystem/portefeuille" element={<WalletPage key="ecosystem-wallet" />} />
              <Route path="/ecosystem/cinema" element={<CinemaTicketsPage key="ecosystem-cinema" />} />
              <Route path="/ecosystem/cinema/:cinemaId" element={<CinemaDetailPage key="ecosystem-cinema-detail" />} />
              <Route path="/dashboard-cinema" element={
                <ProtectedRoute key="dashboard-cinema">
                  <CinemaManagerPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard-hotels" element={
                <ProtectedRoute key="dashboard-hotels">
                  <HotelsManagerPage />
                </ProtectedRoute>
              } />
              <Route path="/ecosystem/tourisme" element={<TourismTicketsPage key="ecosystem-tourisme" />} />
              <Route path="/ecosystem/tourisme/:siteId" element={<TourismSiteDetailPage key="ecosystem-tourisme-detail" />} />
              <Route path="/dashboard-tourisme" element={
                <ProtectedRoute key="dashboard-tourisme">
                  <TourismManagerPage />
                </ProtectedRoute>
              } />
              <Route path="/recharger/:cardNumber" element={<RemoteWalletTopUpPage key="remote-topup" />} />
              <Route path="/ecosystem/encaisser" element={<MerchantCollectPage key="merchant-collect" />} />
              <Route path="/dashboard-business" element={
                <ProtectedRoute key="dashboard-business">
                  <DashboardBusinessPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard-tickets" element={
                <ProtectedRoute key="dashboard-tickets">
                  <TicketScannerPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard-invites/:activityId" element={
                <ProtectedRoute key="dashboard-invites">
                  <ActivityInvitesPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard-sale-qr/:activityId" element={
                <ProtectedRoute key="dashboard-sale-qr">
                  <ActivitySaleQrPage />
                </ProtectedRoute>
              } />
              <Route path="/dashboard-activity-tickets/:activityId" element={
                <ProtectedRoute key="dashboard-activity-tickets">
                  <ManageActivityTicketsPage />
                </ProtectedRoute>
              } />
              <Route path="/invite/:token" element={<InviteClaimPage key="invite-claim" />} />
              <Route path="/t/:code" element={<PublicTicketSalePage key="public-ticket-sale" />} />
              <Route path="/dashboard-cash-agent" element={
                <ProtectedRoute key="dashboard-cash-agent" requiredRoles={['cash_agent', 'admin']}>
                  <DashboardCashAgentPage />
                </ProtectedRoute>
              } />
              <Route path="/payer/:merchantCode" element={<MerchantPayPage key="merchant-pay" />} />
              <Route path="/scan-payer" element={
                <ProtectedRoute key="scan-payer">
                  <MerchantScanPage />
                </ProtectedRoute>
              } />
              <Route path="/news" element={<NewsPage key="news" />} />
              <Route path="/news/:id" element={<NewsPage key={`news-${location.pathname}`} />} />
              <Route path="/activities" element={<ActivitiesPage key="activities" />} />
              <Route path="/allo" element={<AlloPage key="allo" />} />
              <Route path="/email-verification" element={<EmailVerificationPage key="email-verification" />} />
              <Route path="/register-taxi" element={<TaxiRegistrationPage key="register-taxi" />} />
              <Route path="/terms-taxi" element={<TaxiTermsPage key="terms-taxi" />} />
              <Route path="/terms-rental" element={<RentalTermsPage key="terms-rental" />} />
              <Route path="/terms-activity" element={<ActivityTermsPage key="terms-activity" />} />
              <Route path="/terms-business" element={<BusinessTermsPage key="terms-business" />} />
              <Route path="/terms-agent" element={<CommercialAgentTermsPage key="terms-agent" />} />
              <Route path="/login" element={<LoginPage key="login" />} />
              <Route path="/reset-password" element={<ResetPasswordPage key="reset-password" />} />
              <Route path="/complete-profile" element={<CompleteProfilePage key="complete-profile" />} />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute key="profile">
                    <ProfilePage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/marketing" 
                element={
                  <ProtectedRoute key="marketing">
                    <MarketingPage />
                  </ProtectedRoute>
                } 
              />
              <Route path="/admin-setup" element={<Navigate to="/" replace />} />
              <Route path="/admin-fix" element={<Navigate to="/" replace />} />
              <Route path="/admin" element={<Navigate to="/" replace />} />
              <Route path="/admin-role-manager" element={<Navigate to="/" replace />} />
              <Route 
                path={ADMIN_ROLE_MANAGER_PATH}
                element={
                  <ProtectedRoute key="admin-role-manager" adminOnly={true}>
                    <AdminRoleManagerPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/select-dashboard" 
                element={
                  <ProtectedRoute key="select-dashboard">
                    <SelectDashboardPage />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard-client" 
                element={
                  <ProtectedRoute key="dashboard-client">
                    <DashboardClient />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/dashboard-driver" 
                element={<DashboardDriver key="dashboard-driver" />}
              />
              <Route 
                path={ADMIN_PANEL_PATH}
                element={
                  <ProtectedRoute key="admin" adminOnly={true}>
                    <AdminPanel />
                  </ProtectedRoute>
                } 
              />
              <Route path="/register-business" element={<BusinessRegistrationPage key="register-business" />} />
              <Route path="/bookings" element={<BookingsPage key="bookings" />} />
              <Route path="/events" element={<EventsPage key="events" />} />
              <Route path="/location-voiture" element={<CarRentalPage key="location-voiture" />} />
              <Route
                path="/register-rental"
                element={
                  <ProtectedRoute key="register-rental">
                    <CarRentalRegistrationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard-rental"
                element={
                  <ProtectedRoute key="dashboard-rental">
                    <DashboardRentalPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/search" element={<SearchPage key="search" />} />
              <Route path="/contact" element={<ContactPage key="contact" />} />
              <Route
                path="/register-activity"
                element={
                  <ProtectedRoute key="register-activity">
                    <ActivityRegistrationPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/activity/:id" element={<ActivityDetailPage key="activity-detail" />} />
              <Route path="/business/:id" element={<BusinessDetailPage key="business-detail" />} />
            </Routes>
                        </Suspense>
                      </ErrorBoundary>
                    </main>
                    <ResponsiveFooter />
                    <ModernBottomNav />
                    <BackToTopButton />
                    <AIAssistant />
                    <KeyboardShortcuts enabled={true} />
                    <InactivityTimer />
                    <Toaster 
                      position="top-right"
                      toastOptions={{
                        duration: 4000,
                        style: {
                          background: '#363636',
                          color: '#fff',
                        },
                        success: {
                          duration: 3000,
                          style: {
                            background: '#10b981',
                          },
                        },
                        error: {
                          duration: 5000,
                          style: {
                            background: '#ef4444',
                          },
                        },
                      }}
                    />
                  </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AIAssistantProvider>
          <AuthProvider>
            <WalletProvider>
            <MarketingProvider>
              <ChatProvider>
                <ThemeProvider>
              <NotificationProvider>
                <BusinessProvider>
                  <ReviewProvider>
                    <FavoritesProvider>
                      <PushNotificationProvider>
                        <BookingProvider>
                          <EventProvider>
                            <AdvertisementProvider>
                              <PartnerProvider>
                                <TestimonialProvider>
                                <ActivityProvider>
                                  <NewsProvider>
                                    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                                      <AppContent />
                                    </Router>
                                  </NewsProvider>
                                </ActivityProvider>
                                </TestimonialProvider>
                              </PartnerProvider>
                            </AdvertisementProvider>
                          </EventProvider>
                        </BookingProvider>
                      </PushNotificationProvider>
                    </FavoritesProvider>
                  </ReviewProvider>
                </BusinessProvider>
              </NotificationProvider>
            </ThemeProvider>
          </ChatProvider>
          </MarketingProvider>
            </WalletProvider>
        </AuthProvider>
        </AIAssistantProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
